import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, viewChild } from '@angular/core';
import { EAbaPreMedico, EAbaConsultorioPrivado, EAbaPrePrestador } from '@/features/servicos/shared/enums/abas-servicos.enum';
import { FormsModule } from '@angular/forms';
import { EmailPessoaFisicaService } from '@/core/api/sicom-api/servicos/pessoa-fisica/email-pessoa-fisica/email-pessoa-fisica.service';
import { EmailService } from '@/core/api/sicom-api/sistema/email/email.service';
import { AuthService } from '@/core/auth/auth.service';
import { MenuItem } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { EmailComponent, EmailData } from '@/shared/components/contato/components/email/email.component';
import { PreMedicoServicos } from '@/features/servicos/pessoa-fisica/content/pre-medico-servicos/pre-medico-servicos';
import { PrePrestadorServicos } from '@/features/servicos/pessoa-juridica/content/pre-prestador-servicos/pre-prestador-servicos';
import { VistoFracionadoServicos } from '@/features/servicos/pessoa-fisica/content/visto-servicos/visto-fracionado-servicos';
import { PcmsoServicos } from '@/features/servicos/pessoa-fisica/content/pcmso-servicos/pcmso-servicos';
import { ConsultorioPrivadoServicos } from '@/features/servicos/pessoa-fisica/content/consultorio-privado-servicos/consultorio-privado-servicos';
import { ConsultorioPrivadoCadastroServicos } from '@/features/servicos/pessoa-fisica/content/consultorio-privado-servicos/consultorio-privado-cadastro-servicos';
import { PosGraduacaoServicos } from '@/features/servicos/pessoa-fisica/content/pos-graduacao-servicos/pos-graduacao-servicos';
import { DiretorClinicoServicos } from '@/features/servicos/pessoa-fisica/content/diretor-clinico-servicos/diretor-clinico-servicos';
import { AverbacaoNomeServicos } from '@/features/servicos/pessoa-fisica/content/averbacao-nome-servicos/averbacao-nome-servicos';
import { AverbacaoNacionalidadeServicos } from '@/features/servicos/pessoa-fisica/content/averbacao-nacionalidade-servicos/averbacao-nacionalidade-servicos';
import { CancelamentoFalecimentoServicos } from '@/features/servicos/pessoa-fisica/content/cancelamento-falecimento-servicos/cancelamento-falecimento-servicos';
import { CancelamentoInscricaoServicos } from '@/features/servicos/pessoa-fisica/content/cancelamento-inscricao-servicos/cancelamento-inscricao-servicos';
import { CarteiraProfissionalServicos } from '@/features/servicos/pessoa-fisica/content/carteira-profissional-servicos/carteira-profissional-servicos';
import { CedulaIdentidadeServicos } from '@/features/servicos/pessoa-fisica/content/cedula-identidade-servicos/cedula-identidade-servicos';
import { EspecialidadeMedicaServicos } from '@/features/servicos/pessoa-fisica/content/especialidade-medica-servicos/especialidade-medica-servicos';
import { AreaAtuacaoServicos } from '@/features/servicos/pessoa-fisica/content/area-atuacao-servicos/area-atuacao-servicos';
import { CartEventosServicos } from '@/features/servicos/pessoa-juridica/content/cart-eventos-servicos/cart-eventos-servicos';
import { SolicitacaoDiretorServicos } from '@/features/servicos/pessoa-juridica/content/solicitacao-diretor-servicos/solicitacao-diretor-servicos';
import { SolicitacaoDiretorTecnicoServicos } from '@/features/servicos/pessoa-juridica/content/solicitacao-diretor-tecnico-servicos/solicitacao-diretor-tecnico-servicos';
import { RenovacaoServicos } from '@/features/servicos/pessoa-juridica/content/renovacao-servicos/renovacao-servicos';
import { CancelamentoPjServicos } from '@/features/servicos/pessoa-juridica/content/cancelamento-pj-servicos/cancelamento-pj-servicos';
import { AlteracaoCadastralServicos } from '@/features/servicos/pessoa-juridica/content/alteracao-cadastral-servicos/alteracao-cadastral-servicos';
import { RegimentoInternoServicos } from '@/features/servicos/pessoa-juridica/content/regimento-interno-servicos/regimento-interno-servicos';
import { ComissaoEticaServicos } from '@/features/servicos/pessoa-juridica/content/comissao-etica-servicos/comissao-etica-servicos';
import { DashboardServicosFacade } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.facade';
import { UfEfetivaStore } from '@/features/servicos/shared/uf-efetiva.store';
import { PermissionService } from '@/core/permissions/permission.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { formatarDataHoraMascara, montarRequestProtocolo, valorTexto } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { acoesOcultasPorUf, campoDaConfirmacao, chaveDaDataDeConfirmacao, rolesDoServico } from '@/features/servicos/shared/utils/servicos-acoes.utils';
import { linhaDeTramitacaoDoAviso, textoPlanoDeHtml } from '@/features/servicos/shared/utils/aviso-pendencia.presenter';
import { efetivacaoSeraExOfficio, mensagemConfirmacaoExOfficio } from '@/features/servicos/shared/utils/ex-officio.presenter';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CardContent } from '@/shared/components/card-content/card-content';
import { EMPTY, catchError, forkJoin, of } from 'rxjs';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
export type CardServicoAcao = 'informacoes' | 'historico' | 'visualizar' | 'avisar-pendencia' | 'cancelar' | 'confirmar';

export interface DiretorTecnico {
    nome: string;
    cpf: string;
    crm: string;
    uf: string;
}

export interface ProtocoloResumo {
    portal: string;
    sas?: string;
    avatar?: string;
    numeroProtocolo?: string;
    anoProtocolo?: string;
    ufCrm?: string;
    /** CNPJ já mascarado. É o que identifica o solicitante quando ele é pessoa jurídica e ainda não tem CRM. */
    cnpj?: string;
    /**
     * Diretor técnico da empresa. Na pessoa jurídica é ELE quem o avatar do card retrata — o sujeito
     * do protocolo é a empresa, mas quem está sob análise é o médico responsável pelo cadastro.
     */
    diretorTecnicoNome?: string;
    diretorTecnicoCrmUf?: string;
    situacaoCodigo?: string;
    /**
     * Abertura da solicitação no portal — a data a partir da qual a inércia da empresa é contada.
     *
     * Não confundir com `ultimaTramitacao.dataTramitacao`, que é o último movimento INTERNO do
     * protocolo: aquela avança a cada e-mail, anotação ou troca de coluna, e é justamente o
     * protocolo esquecido há meses que mais recebe tramitação. Contar o prazo do ex officio por ela
     * faria a empresa parada há 90 dias parecer nova a cada aviso enviado, e o ato que só o decurso
     * de prazo autoriza nunca ficaria disponível — ou ficaria na hora errada.
     */
    dataSolicitacao?: string;
    inPago?: boolean;
    isAnuidadePaga?: boolean;
    modalidadeInscricao?: string;
    indicacaoDiretorTecnico?: 'ACEITOU' | 'RECUSOU' | 'AGUARDANDO' | 'NAO_SOCIO';
    ultimaTramitacao?: TramitacaoHistoricoCard;
    status?: 'SEM INDICAÇÃO' | 'ACEITOU' | 'RECUSOU' | 'AGUARDANDO';
}

export interface TramitacaoHistoricoCard {
    numeroProtocolo?: string;
    anoProtocolo?: string;
    ufProtocolo?: string;
    textoInformativo?: string;
    dataTramitacao?: string;
}

export interface InserirHistoricoPayload {
    textoInformativo: string;
}

export interface ConfirmarSolicitacaoPayload {
    dtCancelamento?: string;
    dtHomologacao?: string;
    dataDiretoria?: string;
    observacao?: string;
}

export interface VisibilidadeAbasConsultorio {
    mostrarCompartilhamento: boolean;
    mostrarEspecialidades: boolean;
}

type AbaDashboardServicos = EAbaPrePrestador | EAbaPreMedico | EAbaConsultorioPrivado;

const ABAS_PRE_MEDICO: readonly EAbaPreMedico[] = Object.values(EAbaPreMedico);
const ABAS_PRE_PRESTADOR: readonly EAbaPrePrestador[] = Object.values(EAbaPrePrestador);
const ABAS_CONSULTORIO_PRIVADO: readonly EAbaConsultorioPrivado[] = Object.values(EAbaConsultorioPrivado);

type DialogAcaoServico = 'cancelar' | 'confirmar' | null;

/** Card "switchboard" dos Serviços: renderiza o form do serviço selecionado e expõe o menu de ações (histórico/visualizar/confirmar/cancelar) com visibilidade por permissão. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-card-servicos',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        ButtonModule,
        DialogModule,
        FormsModule,
        BotaoAcoes,
        EmailComponent,
        PreMedicoServicos,
        PrePrestadorServicos,
        VistoFracionadoServicos,
        PcmsoServicos,
        ConsultorioPrivadoServicos,
        ConsultorioPrivadoCadastroServicos,
        PosGraduacaoServicos,
        DiretorClinicoServicos,
        AverbacaoNomeServicos,
        AverbacaoNacionalidadeServicos,
        CancelamentoFalecimentoServicos,
        CancelamentoInscricaoServicos,
        CarteiraProfissionalServicos,
        CedulaIdentidadeServicos,
        EspecialidadeMedicaServicos,
        AreaAtuacaoServicos,
        CartEventosServicos,
        SolicitacaoDiretorServicos,
        SolicitacaoDiretorTecnicoServicos,
        RenovacaoServicos,
        CancelamentoPjServicos,
        AlteracaoCadastralServicos,
        RegimentoInternoServicos,
        ComissaoEticaServicos,
        CardContent
    ],
    templateUrl: './card-servicos.html',
    styleUrls: ['./card-servicos.scss']
})
export class CardServicos {
    protected readonly EInconPos = EInconPos;
    readonly showEmail = input(false);
    readonly selectedCodigo = input('');
    readonly protocoloSelecionado = input<ProtocoloResumo | null>(null);
    readonly mostrarVoltar = input(false);
    readonly historicoTramitacoes = input<TramitacaoHistoricoCard[]>([]);
    readonly carregandoHistorico = input(false);
    readonly erroHistorico = input<string | null>(null);
    readonly inserindoHistorico = input(false);
    readonly erroInsercaoHistorico = input<string | null>(null);
    readonly confirmandoSolicitacao = input(false);
    readonly sucessoConfirmacaoSolicitacao = input<string | null>(null);
    /**
     * Recusa da finalização (efetivar ou cancelar) devolvida pelo servidor.
     *
     * Entra aqui, e não só como toast, porque `confirmarDialogAcao()` fecha o diálogo no MESMO clique:
     * a recusa chega depois, quando já não há diálogo onde escrevê-la. E o toast do interceptor central
     * só sai quando o corpo do erro traz texto — um 403 do filtro de segurança chega vazio, e sem este
     * banner o clique não produziria reação alguma na tela.
     */
    readonly erroConfirmacaoSolicitacao = input<string | null>(null);
    /** CRM atribuído pela efetivação que acabou de ocorrer. Nulo nos serviços que não geram registro. */
    readonly crmGeradoConfirmacao = input<number | null>(null);
    readonly abaPrePrestadorSelecionada = input<AbaDashboardServicos>(EAbaPrePrestador.DADOS);
    readonly exibirAcoes = input(true);
    readonly unidadeNome = input('');
    readonly codigoEstado = input('');
    readonly tramitadoPor = input('');
    readonly solicitadoEm = input('');
    readonly ultimaTramitacao = input('');
    readonly diretorQueSolicitou = input<DiretorTecnico | null>(null);
    readonly diretorIndicado = input<DiretorTecnico | null>(null);
    readonly aceitouIndicacao = input<'Sim' | 'Recusou' | 'Aguardando Resposta' | ''>('');
    /**
     * Pedido de aviso de pendência vindo do quadro. `model` porque a resposta é desligá-lo: o card
     * abre o compositor e devolve `false`, senão reabrir o mesmo protocolo dispararia o e-mail
     * outra vez, sem ninguém ter pedido.
     */
    readonly avisoPendenciaSolicitado = model(false);

    readonly informacoes = output<void>();
    readonly historico = output<void>();
    readonly visualizar = output<void>();
    readonly avisarPendencia = output<void>();
    readonly cancelarSolicitacao = output<void>();
    readonly confirmarSolicitacao = output<ConfirmarSolicitacaoPayload>();
    readonly inserirHistorico = output<InserirHistoricoPayload>();
    readonly abaSelecionadaExterna = input<EAbaConsultorioPrivado | null>(null);
    readonly visibilidadeAbasConsultorioAlterada = output<VisibilidadeAbasConsultorio>();
    readonly voltar = output<void>();

    readonly showEmailDialog = signal(false);
    readonly carregandoTemplateEmail = signal(false);
    readonly dadosIniciaisEmail = signal<Partial<EmailData>>({});
    readonly dataHoraSolicitacaoAverbacao = signal('');
    readonly activeAction = signal<CardServicoAcao>('visualizar');
    readonly exibirDialogHistorico = signal(false);
    readonly exibirDialogAcao = signal(false);
    readonly dialogAcaoSelecionada = signal<DialogAcaoServico>(null);
    readonly dataCancelamentoFalecimento = signal('');
    readonly observacaoConfirmacao = signal('');
    readonly novoHistoricoTexto = signal('');
    /**
     * O formulário editável em tela, quando o serviço tem um. Só o pré-prestador e o pré-médico têm:
     * os demais detalhamentos são de leitura.
     *
     * `viewChild` porque o menu de Ações vive AQUI e a edição vive no formulário — este componente é
     * quem renderiza os dois, então fala direto com eles em vez de mandar o pedido subir até o
     * dashboard e voltar.
     */
    readonly prePrestadorEmTela = viewChild(PrePrestadorServicos);
    readonly preMedicoEmTela = viewChild(PreMedicoServicos);

    /** O formulário em tela e a role que autoriza editá-lo, ou `null` num detalhamento de leitura. */
    private readonly formularioEditavel = computed(() => {
        const prePrestador = this.prePrestadorEmTela();
        if (prePrestador) {
            return { formulario: prePrestador, permissao: PERMISSOES.pessoaJuridica.prePrestador.editar };
        }

        const preMedico = this.preMedicoEmTela();
        return preMedico ? { formulario: preMedico, permissao: PERMISSOES.pessoaFisica.preMedico.editar } : null;
    });

    /**
     * A efetivação deste protocolo já ocorreu NESTA tela. O detalhe só permanece aberto (em vez de
     * voltar para a lista) quando a confirmação gerou um CRM — e é esse mesmo sinal que avisa que
     * uma SEGUNDA chamada geraria um SEGUNDO CRM para o mesmo protocolo, órfão do primeiro, sem que
     * nada na tela pareça diferente de antes do primeiro clique (a lista já não recua mais para
     * provar que deu certo). Mesmo problema resolvido no quadro, em `kanban-protocolos.ts`
     * (`conclusoesAceitasNoPortal`) — aqui o gatilho é mais simples porque já existe: ter CRM.
     */
    private readonly efetivacaoConcluidaNestaTela = computed(() => this.crmGeradoConfirmacao() !== null);

    /**
     * O botão "Confirmar Solicitação" deste protocolo vai praticar a inscrição de ofício.
     *
     * Quem decide é o presenter, e não este componente: o quadro faz a MESMA pergunta sobre o mesmo
     * protocolo, e duas contas de prazo escritas em lugares diferentes é como o botão passa a aceitar
     * o que o arraste recusa. Aqui entra só o que o presenter não tem como saber — o código do
     * serviço em tela e a resposta do `PermissionService` para a UF deste protocolo.
     *
     * O `Date.now()` fica FORA do presenter pelo mesmo motivo: a regra recebe "hoje" para poder ser
     * testada sem relógio.
     */
    private readonly confirmacaoSeraExOfficio = computed(() =>
        efetivacaoSeraExOfficio(this.selectedCodigo(), this.protocoloSelecionado(), Date.now(), () => this.permissionService.can(PERMISSOES.pessoaJuridica.prePrestador.exOfficio, { uf: this.protocoloSelecionado()?.ufCrm }))
    );

    /**
     * Menu de ações derivado dos inputs: `disabled`/`visible` de Confirmar/Cancelar reagem ao
     * protocolo, serviço e permissões.
     *
     * <p>Onda de correção final (Correção 4, severidade subida pela revisão): as SEIS ações daqui são
     * Editar, Histórico, Visualizar, Avisar Pendência, Cancelar Solicitação e Confirmar Solicitação —
     * o comentário anterior ("fecha confirmar, cancelar e avisar pendencia de uma vez") enumerava só
     * três, e foi essa enumeração incompleta que escondeu o defeito: `if (somenteLeitura()) return [];`
     * também apagava Histórico e Visualizar, que são LEITURA. O pedido do usuário foi literal — "ele
     * pode apenas visualizar o andamento" — e devolver `[]` apagava justamente o que ele pode fazer.
     * Por isso agora cada ação de ESCRITA (Editar, Avisar Pendência, Cancelar, Confirmar) carrega a
     * própria condição `!somenteLeitura`, e as duas de leitura ficam incondicionais. `Editar` conta
     * como escrita mesmo sendo do formulário, não do protocolo — habilita edição de dados.</p>
     */
    readonly itensAcoesMenu = computed<MenuItem[]>(() => {
        const somenteLeitura = this.ufEfetivaStore.somenteLeitura();
        const uf = this.protocoloSelecionado()?.ufCrm;
        const roles = rolesDoServico(this.selectedCodigo());
        const cartEventoForaDoRj = acoesOcultasPorUf(this.selectedCodigo(), uf);
        // Fail-closed quando o codigo esta mapeado: sem a role da acao, a acao fica escondida
        // (servico so-cancelar nao mostra "Confirmar" inerte, pois nao tem efetivar). Codigo
        // nao-mapeado (roles undefined) mantem o default aberto.
        const confirmarVisivel = !cartEventoForaDoRj && (roles ? !!roles.confirmar && this.permissionService.can(roles.confirmar, { uf }) : true);
        const cancelarVisivel = !cartEventoForaDoRj && (roles ? !!roles.cancelar && this.permissionService.can(roles.cancelar, { uf }) : true);
        // "Editar" é do FORMULÁRIO, não do protocolo: só aparece onde há formulário editável em tela,
        // e some enquanto a edição está aberta — ali quem manda são Salvar/Cancelar, dentro do form.
        //
        // Fora das abas editáveis o item fica DESABILITADO em vez de sumir: item que desaparece ao
        // trocar de aba se lê como defeito, enquanto o apagado diz que a ação existe e só não vale ali.
        const editavel = this.formularioEditavel();

        return [
            // Entra na lista SÓ quando há formulário editável em tela, a edição ainda não começou e o
            // perfil não é o nacional — item inerte com `visible: false` continuaria no array, e o
            // guard de permissões do `app-botao-acoes` cobra `permission` de toda ação de escrita, que
            // aqui só existe quando se sabe QUAL formulário está aberto.
            ...(!somenteLeitura && editavel && !editavel.formulario.editando()
                ? [
                      {
                          label: 'Editar',
                          icon: 'pi pi-pencil',
                          styleClass: 'bg-blue-500',
                          disabled: !editavel.formulario.abaEditavel(),
                          permission: editavel.permissao,
                          command: () => editavel.formulario.habilitarEdicao()
                      }
                  ]
                : []),
            { label: 'Histórico', icon: 'pi pi-history', styleClass: 'bg-surface-700', command: () => this.onAcaoClick('historico') },
            { label: 'Visualizar', icon: 'pi pi-eye', styleClass: 'bg-cyan-500', command: () => this.onAcaoClick('visualizar') },
            ...(somenteLeitura ? [] : [{ label: 'Avisar Pendência', icon: 'pi pi-comments', styleClass: 'bg-orange-500', command: () => this.onAcaoClick('avisar-pendencia') }]),
            ...(somenteLeitura ? [] : [{ label: 'Cancelar Solicitação', icon: 'pi pi-ban', styleClass: 'bg-red-500', disabled: this.efetivacaoConcluidaNestaTela(), visible: cancelarVisivel, command: () => this.onAcaoClick('cancelar') }]),
            ...(somenteLeitura ? [] : [{ label: 'Confirmar Solicitação', icon: 'pi pi-check', styleClass: 'bg-green-500', disabled: this.isConfirmacaoDesabilitada(), visible: confirmarVisivel, command: () => this.onAcaoClick('confirmar') }])
        ];
    });

    private readonly emailPessoaFisicaService = inject(EmailPessoaFisicaService);
    private readonly emailService = inject(EmailService);
    private readonly authService = inject(AuthService);
    private readonly messageService = inject(MessageService);
    private readonly dashboardServicosFacade = inject(DashboardServicosFacade);
    private readonly permissionService = inject(PermissionService);
    private readonly ufEfetivaStore = inject(UfEfetivaStore);

    constructor() {
        // Ao trocar de serviço ou protocolo, limpa a data/hora digitada (evita vazar valor entre solicitações).
        effect(() => {
            this.selectedCodigo();
            this.protocoloSelecionado();
            this.dataHoraSolicitacaoAverbacao.set('');
        });

        // O quadro moveu o card para pendências pedindo o aviso: abre o mesmo compositor da ação
        // "Avisar Pendência" do menu, com o protocolo que o quadro acabou de selecionar. Espera o
        // protocolo chegar — o pedido e a seleção vêm no mesmo ciclo, e sem protocolo o e-mail sairia
        // sem destinatário nem template.
        effect(() => {
            if (!this.avisoPendenciaSolicitado() || !this.protocoloSelecionado()) {
                return;
            }
            this.avisoPendenciaSolicitado.set(false);
            this.avisoNascidoDoQuadro = true;
            this.onAcaoClick('avisar-pendencia');
        });
    }

    /**
     * O aviso em curso veio do quadro (checkbox do movimento), e não do menu de ações.
     *
     * Campo simples, e não signal: nada na tela o lê — ele só sobrevive entre abrir o compositor e o
     * envio terminar, para decidir se a linha entra na tramitação local do quadro.
     */
    private avisoNascidoDoQuadro = false;

    openEmail(): void {
        // Ensure dialog reopens even if it was already open before
        if (this.showEmailDialog()) {
            this.showEmailDialog.set(false);
            setTimeout(() => this.showEmailDialog.set(true));
        } else {
            this.showEmailDialog.set(true);
        }
    }

    onSendEmail(data: EmailData): void {
        const destinatarios = this.extrairDestinatarios(data.to);
        if (!destinatarios.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Aviso',
                detail: 'Informe ao menos um destinatario valido.'
            });
            return;
        }

        const payload = {
            to: destinatarios,
            subject: data.subject,
            html: data.content || ''
        };

        const envio$ = data.attachments?.length
            ? this.emailService.enviarHtmlComAnexos({
                  ...payload,
                  attachments: data.attachments
              })
            : this.emailService.enviarHtml(payload);

        // A mensagem de falha é do interceptor central; aqui o diálogo apenas permanece aberto para
        // nova tentativa. `EMPTY` encerra o fluxo sem deixar erro não tratado no RxJS.
        envio$.pipe(catchError(() => EMPTY)).subscribe({
            next: () => {
                this.registrarPendenciaAposEmail(this.montarTextoHistoricoEmail(data), data);

                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Email enviado com sucesso.'
                });
                this.showEmailDialog.set(false);
            }
        });
    }

    onEmailGoBack(): void {
        this.showEmailDialog.set(false);
    }

    get abaPreMedicoSelecionadaExterna(): EAbaPreMedico | null {
        return ABAS_PRE_MEDICO.includes(this.abaPrePrestadorSelecionada() as EAbaPreMedico) ? (this.abaPrePrestadorSelecionada() as EAbaPreMedico) : null;
    }

    get abaPrePrestadorSelecionadaExterna(): EAbaPrePrestador | null {
        return ABAS_PRE_PRESTADOR.includes(this.abaPrePrestadorSelecionada() as EAbaPrePrestador) ? (this.abaPrePrestadorSelecionada() as EAbaPrePrestador) : null;
    }

    get abaConsultorioPrivadoSelecionadaExterna(): EAbaConsultorioPrivado | null {
        return ABAS_CONSULTORIO_PRIVADO.includes(this.abaPrePrestadorSelecionada() as EAbaConsultorioPrivado) ? (this.abaPrePrestadorSelecionada() as EAbaConsultorioPrivado) : null;
    }

    get labelMetaSolicitacao(): string {
        if (this.selectedCodigo() === 'averbacao-nome') {
            return 'Data/Hora Solicitacao:';
        }

        return this.solicitadoEm() ? 'Solicitado Em:' : 'Ultima tramitacao:';
    }

    get valorMetaSolicitacao(): string {
        if (this.selectedCodigo() === 'averbacao-nome') {
            return this.dataHoraSolicitacaoAverbacao() || '-';
        }

        return this.solicitadoEm() || this.ultimaTramitacaoResumo || '-';
    }

    get ultimaTramitacaoResumo(): string {
        if (this.ultimaTramitacao()) {
            return this.ultimaTramitacao();
        }

        return this.formatarTramitacaoResumo(this.protocoloSelecionado()?.ultimaTramitacao);
    }

    onDataHoraSolicitacaoAtualizada(valor: string): void {
        this.dataHoraSolicitacaoAverbacao.set(formatarDataHoraMascara(valor));
    }

    onAcaoClick(acao: CardServicoAcao): void {
        switch (acao) {
            case 'informacoes':
                this.activeAction.set(acao);
                this.informacoes.emit();
                break;
            case 'historico':
                this.activeAction.set(acao);
                this.historico.emit();
                break;
            case 'visualizar':
                this.activeAction.set(acao);
                this.visualizar.emit();
                break;
            case 'avisar-pendencia':
                // Mesmo precedente do `case 'cancelar'`, logo abaixo: `itensAcoesMenu` já fecha esta
                // ação para o nacional, mas isso é aparência de menu — o efeito que reage ao pedido de
                // aviso vindo do quadro chama `onAcaoClick('avisar-pendencia')` direto, sem passar por
                // `itensAcoesMenu`, e uma chamada direta (atalho, teste, código futuro) tem de
                // respeitar a mesma regra.
                if (this.ufEfetivaStore.somenteLeitura()) {
                    return;
                }
                if (this.showEmail()) {
                    this.abrirAvisoPendenciaComTemplate();
                } else {
                    this.avisarPendencia.emit();
                }
                break;
            case 'cancelar':
                // `disabled` no item do menu é aparência: chamada direta (atalho, teste, código
                // futuro) tem de respeitar a mesma regra, senão um protocolo já efetivado nesta tela
                // seria cancelado por um caminho que não passa pelo menu.
                if (this.efetivacaoConcluidaNestaTela()) {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Aviso',
                        detail: 'A solicitação já foi efetivada nesta tela; cancelar não é mais possível.'
                    });
                    return;
                }
                this.abrirDialogAcao('cancelar');
                break;
            case 'confirmar':
                if (this.isConfirmacaoDesabilitada()) {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Aviso',
                        detail: 'A confirmação da solicitação está desabilitada para este protocolo.'
                    });
                    return;
                }
                this.abrirDialogAcao('confirmar');
                break;
        }
    }

    statusClasse(status?: ProtocoloResumo['status']): string {
        switch (status) {
            case 'ACEITOU':
                return 'accepted';
            case 'RECUSOU':
                return 'rejected';
            case 'AGUARDANDO':
                return 'waiting';
            default:
                return 'no-indication';
        }
    }

    formatarDataTramitacao(valor?: string): string {
        if (!valor) {
            return 'Data não informada';
        }

        const data = new Date(valor);
        if (Number.isNaN(data.getTime())) {
            return valor;
        }

        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(data);
    }

    private formatarTramitacaoResumo(tramitacao?: TramitacaoHistoricoCard): string {
        if (!tramitacao) {
            return '';
        }

        return this.formatarDataTramitacao(tramitacao.dataTramitacao);
    }

    abrirDialogHistorico(): void {
        this.exibirDialogHistorico.set(true);
    }

    fecharDialogHistorico(): void {
        this.exibirDialogHistorico.set(false);
        this.novoHistoricoTexto.set('');
    }

    atualizarTextoHistorico(event: Event): void {
        this.novoHistoricoTexto.set((event.target as HTMLTextAreaElement).value ?? '');
    }

    cancelarNovoHistorico(): void {
        this.fecharDialogHistorico();
    }

    salvarNovoHistorico(): void {
        const textoInformativo = this.novoHistoricoTexto().trim();
        if (!textoInformativo) {
            return;
        }

        this.inserirHistorico.emit({
            textoInformativo
        });

        this.fecharDialogHistorico();
    }

    abrirDialogAcao(acao: Exclude<DialogAcaoServico, null>): void {
        this.dialogAcaoSelecionada.set(acao);
        this.exibirDialogAcao.set(true);
    }

    fecharDialogAcao(): void {
        this.exibirDialogAcao.set(false);
        this.dialogAcaoSelecionada.set(null);
        this.dataCancelamentoFalecimento.set('');
        this.observacaoConfirmacao.set('');
    }

    /**
     * O título nomeia o ato, e não o gesto: o analista clicou no mesmo item de menu de sempre, e é
     * neste diálogo que ele descobre que o efeito é outro. "Confirmar Solicitação" sobre uma inscrição
     * de ofício descreveria um ato que não é o que vai acontecer.
     */
    tituloDialogAcao(): string {
        switch (this.dialogAcaoSelecionada()) {
            case 'cancelar':
                return 'Cancelar Solicitacao';
            case 'confirmar':
                return this.confirmacaoSeraExOfficio() ? 'Inscrever de Ofício' : 'Confirmar Solicitacao';
            default:
                return 'Acao';
        }
    }

    /**
     * O corpo do diálogo. No ex officio ele vem do presenter, com os dias de inércia e as
     * consequências — CRM provisório e prazo de regularização —, porque "deseja realmente confirmar"
     * não é consentimento a um ato que ninguém nomeou.
     */
    mensagemDialogAcao(): string {
        switch (this.dialogAcaoSelecionada()) {
            case 'cancelar':
                return 'Deseja realmente cancelar a solicitacao?';
            case 'confirmar':
                return this.confirmacaoSeraExOfficio() ? mensagemConfirmacaoExOfficio(this.protocoloSelecionado() ?? {}, Date.now()) : 'Deseja realmente confirmar a solicitacao?';
            default:
                return '';
        }
    }

    onVisibilidadeAbasConsultorioAlterada(visibilidade: VisibilidadeAbasConsultorio): void {
        this.visibilidadeAbasConsultorioAlterada.emit(visibilidade);
    }

    confirmarDialogAcao(): void {
        if (this.dialogAcaoSelecionada() === 'confirmar') {
            const chaveData = chaveDaDataDeConfirmacao(this.selectedCodigo());
            const data = this.dataConfirmacaoFormatada();
            this.confirmarSolicitacao.emit({
                dtCancelamento: chaveData === 'dtCancelamento' ? data : undefined,
                dtHomologacao: chaveData === 'dtHomologacao' ? data : undefined,
                dataDiretoria: chaveData === 'dataDiretoria' ? data : undefined,
                observacao: this.isConfirmacaoAlteracaoCadastral() ? this.observacaoConfirmacao().trim() : undefined
            });
        }

        if (this.dialogAcaoSelecionada() === 'cancelar') {
            this.cancelarSolicitacao.emit();
        }

        this.fecharDialogAcao();
    }

    isConfirmacaoComData(): boolean {
        if (this.dialogAcaoSelecionada() !== 'confirmar') {
            return false;
        }

        return this.isServicoComData();
    }

    podeConfirmarAcao(): boolean {
        if (this.dialogAcaoSelecionada() !== 'confirmar') {
            return true;
        }

        if (this.isServicoComData() && !this.dataCancelamentoFalecimento().trim()) {
            return false;
        }
        return !this.isConfirmacaoAlteracaoCadastral() || !!this.observacaoConfirmacao().trim();
    }

    private dataConfirmacaoFormatada(): string | undefined {
        if (!this.isServicoComData()) {
            return undefined;
        }

        const dataIso = this.dataCancelamentoFalecimento().trim();
        if (!dataIso) {
            return undefined;
        }

        const [ano, mes, dia] = dataIso.split('-');
        if (!ano || !mes || !dia) {
            return undefined;
        }

        return `${dia}/${mes}/${ano}`;
    }

    private isServicoComData(): boolean {
        return campoDaConfirmacao(this.selectedCodigo()) === 'data';
    }

    isConfirmacaoAlteracaoCadastral(): boolean {
        return campoDaConfirmacao(this.selectedCodigo()) === 'observacao';
    }

    /**
     * A confirmação está inerte.
     *
     * A ordem das três perguntas é a regra:
     *
     * 1. efetivação JÁ concluída nesta tela barra tudo, inclusive o ex officio — ali o protocolo já
     *    tem CRM, e um segundo clique geraria um SEGUNDO registro para a mesma empresa, órfão do
     *    primeiro. A exceção do ofício é ao gate de PAGAMENTO, nunca a esta;
     * 2. protocolo pago segue pelo caminho de sempre;
     * 3. só então a exceção: sem pagamento, a confirmação existe apenas quando ela é a inscrição de
     *    ofício — decurso de prazo, pendência financeira e a role, tudo conferido pelo presenter.
     */
    private isConfirmacaoDesabilitada(): boolean {
        if (this.efetivacaoConcluidaNestaTela()) {
            return true;
        }

        if (this.protocoloSelecionado()?.inPago === true) {
            return false;
        }

        return !this.confirmacaoSeraExOfficio();
    }

    private abrirAvisoPendenciaComTemplate(): void {
        if (this.carregandoTemplateEmail()) {
            return;
        }

        this.carregandoTemplateEmail.set(true);
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());
        const estrutura$ = this.emailPessoaFisicaService
            .consultarEstrutura({
                nomeSolicitante: this.unidadeNome(),
                conselhoOrgao: this.obterConselhoOrgao(),
                uf: this.obterUfUsuarioLogado()
            })
            .pipe(catchError(() => of(null)));
        const dadosServico$ = request ? this.dashboardServicosFacade.consultarDadosProtocoloPorServico(this.selectedCodigo(), request).pipe(catchError(() => of({} as Record<string, unknown>))) : of({} as Record<string, unknown>);

        forkJoin({ estrutura: estrutura$, dadosServico: dadosServico$ }).subscribe({
            next: ({ estrutura, dadosServico }) => {
                this.dadosIniciaisEmail.set({
                    content: estrutura?.template ?? '',
                    to: this.montarDestinatariosEmail(dadosServico)
                });
                this.carregandoTemplateEmail.set(false);
                this.openEmail();
            },
            error: () => {
                // Rede de segurança: as duas fontes já têm `catchError`, então este ramo não é
                // alcançado por falha HTTP — e a mensagem, se houver, vem do interceptor central.
                // Mantém o fluxo: abre o diálogo mesmo sem template.
                this.carregandoTemplateEmail.set(false);
                this.dadosIniciaisEmail.set({
                    to: ''
                });
                this.openEmail();
            }
        });
    }

    private montarDestinatariosEmail(dadosServico: Record<string, unknown>): string {
        return this.deduplicarDestinatarios([...this.extrairDestinatarios(this.dadosIniciaisEmail().to ?? ''), ...this.extrairEmailsCampo(dadosServico, ['emailDiretorTecnico', 'emailPrestador', 'emailSolicitante'])]).join('; ');
    }

    private extrairEmailsCampo(dadosServico: Record<string, unknown>, campos: string[]): string[] {
        return campos.flatMap((campo) => {
            const valor = this.obterValorPorChaveNormalizada(dadosServico, campo);
            return this.extrairDestinatarios(valorTexto(valor));
        });
    }

    private obterValorPorChaveNormalizada(dados: Record<string, unknown>, campo: string): unknown {
        const campoNormalizado = this.normalizarChave(campo);
        const chave = Object.keys(dados).find((item) => this.normalizarChave(item) === campoNormalizado);
        return chave ? dados[chave] : undefined;
    }

    private normalizarChave(chave: string): string {
        return valorTexto(chave)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]/g, '')
            .toLowerCase();
    }

    private deduplicarDestinatarios(destinatarios: string[]): string[] {
        const vistos = new Set<string>();
        const unicos: string[] = [];

        destinatarios.forEach((destinatario) => {
            const email = destinatario.trim();
            const chave = email.toLowerCase();
            if (!email || vistos.has(chave)) {
                return;
            }

            vistos.add(chave);
            unicos.push(email);
        });

        return unicos;
    }

    private obterConselhoOrgao(): string {
        const uf = this.obterUfUsuarioLogado();
        return uf ? `CRM-${uf}` : '';
    }

    private obterUfUsuarioLogado(): string {
        return this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
    }

    private extrairDestinatarios(destinatariosTexto: string): string[] {
        return (destinatariosTexto ?? '')
            .split(/[;,]/)
            .map((item) => item.trim())
            .filter((item) => !!item);
    }

    // Item T1 (Avisar Pendencia): apos enviar o e-mail, devolve a situacao do
    // protocolo para Pendencia no portal (a tramitacao e registrada pelo backend).
    private registrarPendenciaAposEmail(textoHistorico: string, email: EmailData): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());
        // Consumido aqui, no fim do fluxo: um aviso pedido pelo quadro e abandonado no compositor não
        // pode marcar o PRÓXIMO envio como se tivesse vindo de lá.
        const nascidoDoQuadro = this.avisoNascidoDoQuadro;
        this.avisoNascidoDoQuadro = false;

        if (request) {
            this.dashboardServicosFacade.avisarPendenciaServico(
                {
                    numeroProtocolo: request.numeroProtocolo,
                    anoProtocolo: request.anoProtocolo,
                    ufCrm: request.ufCrm,
                    codigoServico: this.selectedCodigo()
                },
                textoHistorico
            );

            // A linha da tramitação local só existe para o aviso nascido do quadro: é lá que ela é
            // lida, e o protocolo avisado pelo menu pode nem ter card. O e-mail já saiu, então o
            // fato registrado é verdade mesmo se a chamada ao portal falhar depois.
            if (nascidoDoQuadro) {
                // O e-mail INTEIRO, e não o texto que vai para o portal: aquele é o que o analista
                // digitou, e o médico recebeu o template todo. Quem lê a tramitação depois precisa ver
                // o que ele recebeu, não um resumo.
                this.dashboardServicosFacade.registrarLinhaNaTramitacaoLocal(request.numeroProtocolo, request.anoProtocolo, linhaDeTramitacaoDoAviso(email.subject), email.content);
            }
            return;
        }

        if (textoHistorico) {
            this.inserirHistorico.emit({ textoInformativo: textoHistorico });
        }
    }

    private montarTextoHistoricoEmail(data: EmailData): string {
        const textoDigitado = textoPlanoDeHtml(data.typedContent ?? '');
        if (textoDigitado) {
            return textoDigitado;
        }

        const assunto = (data.subject ?? '').trim();
        return assunto ? `Email enviado. Assunto: ${assunto}` : 'Email enviado.';
    }
}
